import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Row, Col } from 'react-bootstrap';
import { FiArrowLeft, FiAlertCircle } from 'react-icons/fi';
import { IoClose } from 'react-icons/io5';
import Input from '../../../components/input';
import PhoneNumberInput from '../../../components/PhoneNumberInput';
import Select from '../../../components/Select';
import Button from '../../../components/button';
import { apiService } from '../../../services/apiService';
import { formatUtcDateTimeForApi } from '../../../utils/helper';
import {
    fetchCustomerDropdown,
    fetchCostCenterDropdown,
    fetchUsersDropdown
} from '../../../store/Slices/dropdownSlice';
import {
    buildPhoneNumberSchema,
    normalizePhoneNumberValue,
    optionalPhoneNumberSchema,
} from '../../../pages/ThreePL/shared/phoneValidation';
import { successNotify, errorNotify } from '../../../utils/toast';
import '../../../styles/TripsList.css';

// Yup Validation Schema
const createTripDrawerSchema = Yup.object().shape({
    start_at: Yup.string()
        .required('Trip Start Date is required'),
    expected_end_at: Yup.string()
        .required('Expected End Date is required')
        .test('end-after-start', 'Expected End Date cannot be earlier than Start Date', function (value) {
            const { start_at } = this.parent;
            if (!start_at || !value) return true;
            return new Date(value).getTime() >= new Date(start_at).getTime();
        }),
    customer_id: Yup.string()
        .required("Customer is required"),
    contract_id: Yup.string()
        .required("Contract is required"),
    vehicle_type_id: Yup.string()
        .required("Vehicle Type is required"),
    cost_center_id: Yup.string()
        .required("Cost Center is required"),
    vehicle_id: Yup.string()
        .required("Vehicle is required"),
    primary_driver_id: Yup.string()
        .required("Primary Driver is required"),
    primary_driver_contact: buildPhoneNumberSchema("Primary Driver Contact is required"),
    secondary_driver_contact: optionalPhoneNumberSchema,
    helper_contact: optionalPhoneNumberSchema,
});

const getFirstDefinedValue = (candidates, fallback = '') => {
    for (const candidate of candidates) {
        if (candidate !== '' && candidate !== null && candidate !== undefined) {
            return candidate;
        }
    }
    return fallback;
};

const extractDriverInfo = (data = {}, partyType = 'primary') => {
    let partyObj = null;
    let fallbackId = null;

    if (partyType === 'primary') {
        partyObj = getFirstDefinedValue([
            data?.primary_driver,
            data?.primaryDriver,
            data?.vehicle?.primary_driver,
            data?.driver,
        ], null);
        fallbackId = getFirstDefinedValue([
            data?.primary_driver_id,
            data?.primaryDriverId,
            data?.vehicle?.primary_driver_id,
            data?.driver_id,
        ], '');
    } else if (partyType === 'secondary') {
        partyObj = getFirstDefinedValue([
            data?.secondary_driver,
            data?.secondaryDriver,
            data?.vehicle?.secondary_driver,
        ], null);
        fallbackId = getFirstDefinedValue([
            data?.secondary_driver_id,
            data?.secondaryDriverId,
            data?.vehicle?.secondary_driver_id,
        ], '');
    } else if (partyType === 'helper') {
        partyObj = getFirstDefinedValue([
            data?.helper,
            data?.vehicle?.helper,
        ], null);
        fallbackId = getFirstDefinedValue([
            data?.helper_id,
            data?.vehicle?.helper_id,
        ], '');
    }

    let id = String(
        getFirstDefinedValue([
            partyObj?.id,
            partyObj?.value,
            partyObj?.user_id,
            fallbackId,
        ], '')
    ).trim();

    if (id === '0') {
        id = '';
    }

    const label = String(
        getFirstDefinedValue([
            partyObj?.username,
            partyObj?.name,
            partyObj?.label,
            partyObj?.email,
            data?.primary_driver_name,
            data?.driver_name,
            data?.driver,
        ], '')
    ).trim();

    const contact = String(
        getFirstDefinedValue([
            partyObj?.mobile,
            partyObj?.phone,
            partyObj?.contact,
            data?.primary_driver_contact,
            data?.primary_driver_mobile,
            data?.driver_contact,
            data?.driver_phone,
            data?.phone,
            data?.mobile,
        ], '')
    ).trim();

    return {
        id,
        label: label || (id ? `Driver ${id}` : ''),
        contact: contact ? normalizePhoneNumberValue(contact) || contact : '',
    };
};

const mapVehicleOptions = (vehicles = []) =>
    (Array.isArray(vehicles) ? vehicles : []).map(v => {
        const regNo = v?.label ?? v?.registration_number ?? v?.name ?? `Vehicle ${v?.id}`;
        const vehicleTypeTitle = v?.vehicle_type?.title;
        return {
            value: String(v?.value ?? v?.id ?? v?.vehicle_id ?? ''),
            label: String(regNo),
            badge: vehicleTypeTitle || '',
            raw: v
        };
    }).filter(o => o.value);

const areVehicleDropdownFiltersReady = (values = {}) => {
    if (!values.contract_id || !values.vehicle_type_id || !values.start_at || !values.expected_end_at) {
        return false;
    }

    return new Date(values.expected_end_at).getTime() >= new Date(values.start_at).getTime();
};

const CreateTripDrawer = ({ isOpen, onClose, onTripCreated }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {
        customerDropdownData = [],
        costCenterDropdownData = [],
        usersDropdownData = [],
        loading: reduxLoading
    } = useSelector((state) => state.dropdowns || {});

    // Dynamic dropdown states for cascading flow
    const [contractOptions, setContractOptions] = useState([]);
    const [contractVehicleTypeOptions, setContractVehicleTypeOptions] = useState([]);
    const [vehicleOptions, setVehicleOptions] = useState([]);
    const [assignedCrew, setAssignedCrew] = useState({ primary: null, secondary: null, helper: null });

    const [customerDetailLoading, setCustomerDetailLoading] = useState(false);
    const [contractVehicleTypeLoading, setContractVehicleTypeLoading] = useState(false);
    const [vehicleOptionsLoading, setVehicleOptionsLoading] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    useEffect(() => {
        if (isOpen) {
            setSubmitError(null);
            dispatch(fetchCustomerDropdown());
            dispatch(fetchCostCenterDropdown());
            dispatch(fetchUsersDropdown());

            const originalBodyOverflow = document.body.style.overflow;
            const originalHtmlOverflow = document.documentElement.style.overflow;

            document.body.style.overflow = "hidden";
            document.documentElement.style.overflow = "hidden";

            return () => {
                document.body.style.overflow = originalBodyOverflow;
                document.documentElement.style.overflow = originalHtmlOverflow;
            };
        }
    }, [isOpen, dispatch]);

    const customerOptions = useMemo(() => {
        return (Array.isArray(customerDropdownData) ? customerDropdownData : []).map(c => ({
            value: String(c?.value ?? c?.id ?? ''),
            label: String(c?.label ?? c?.name ?? c?.customer_name ?? `Customer ${c?.id}`),
            cost_center_id: c?.cost_center_id ?? c?.costCenterId ?? c?.cost_center?.id ?? c?.cost_center?.value ?? '',
            raw: c,
        })).filter(o => o.value);
    }, [customerDropdownData]);

    const costCenterOptions = useMemo(() => {
        return (Array.isArray(costCenterDropdownData) ? costCenterDropdownData : []).map(cc => ({
            value: String(cc?.value ?? cc?.id ?? ''),
            label: String(cc?.label ?? cc?.title ?? cc?.name ?? `Cost Center ${cc?.id}`)
        })).filter(o => o.value);
    }, [costCenterDropdownData]);

    // Merged user options: Base users dropdown + Vehicle assigned crew options
    const userOptions = useMemo(() => {
        const optionMap = new Map();

        (Array.isArray(usersDropdownData) ? usersDropdownData : []).forEach(u => {
            const val = String(u?.value ?? u?.id ?? '').trim();
            const lbl = String(u?.label ?? u?.name ?? u?.username ?? `User ${val}`).trim();
            if (val) {
                optionMap.set(val, {
                    value: val,
                    label: lbl,
                    mobile: u?.mobile || u?.phone || u?.contact || ''
                });
            }
        });

        [assignedCrew.primary, assignedCrew.secondary, assignedCrew.helper].forEach(crew => {
            if (crew?.id) {
                const val = String(crew.id).trim();
                if (!optionMap.has(val)) {
                    optionMap.set(val, {
                        value: val,
                        label: crew.label || `Driver ${val}`,
                        mobile: crew.contact || ''
                    });
                } else if (crew.label || crew.contact) {
                    const existing = optionMap.get(val);
                    optionMap.set(val, {
                        ...existing,
                        label: existing.label || crew.label,
                        mobile: existing.mobile || crew.contact
                    });
                }
            }
        });

        return Array.from(optionMap.values());
    }, [usersDropdownData, assignedCrew]);

    if (!isOpen) return null;

    const initialValues = {
        start_at: '',
        expected_end_at: '',
        customer_id: '',
        contract_id: '',
        vehicle_type_id: '',
        cost_center_id: '',
        vehicle_id: '',
        primary_driver_id: '',
        primary_driver_contact: '',
        secondary_driver_id: '',
        secondary_driver_contact: '',
        helper_id: '',
        helper_contact: '',
    };

    const clearVehicleAndCrewFields = (setFieldValue) => {
        setFieldValue("vehicle_id", "");
        setFieldValue("primary_driver_id", "");
        setFieldValue("primary_driver_contact", "");
        setFieldValue("secondary_driver_id", "");
        setFieldValue("secondary_driver_contact", "");
        setFieldValue("helper_id", "");
        setFieldValue("helper_contact", "");
        setVehicleOptions([]);
        setAssignedCrew({ primary: null, secondary: null, helper: null });
    };

    const fetchVehiclesByCustomer = async (nextValues, setFieldValue) => {
        clearVehicleAndCrewFields(setFieldValue);

        if (!areVehicleDropdownFiltersReady(nextValues)) {
            return;
        }

        setVehicleOptionsLoading(true);
        try {
            const response = await apiService.getDropdownVehicleByCustomer({
                contract_id: nextValues.contract_id,
                vehicle_type_id: nextValues.vehicle_type_id,
                start_at: formatUtcDateTimeForApi(nextValues.start_at),
                expected_end_at: formatUtcDateTimeForApi(nextValues.expected_end_at),
            });

            setVehicleOptions(mapVehicleOptions(response?.data || response || []));
        } catch (err) {
            console.error("Error fetching vehicles by customer:", err);
            errorNotify(err);
        } finally {
            setVehicleOptionsLoading(false);
        }
    };

    const handleTripDateChange = (fieldName, value, values, setFieldValue) => {
        setFieldValue(fieldName, value);
        fetchVehiclesByCustomer({ ...values, [fieldName]: value }, setFieldValue);
    };

    // 1. Customer Change Handler -> Fetches Contracts
    const handleCustomerChange = async (option, setFieldValue, setFieldError) => {
        const custId = option ? option.value : "";
        const costCenterId = option?.cost_center_id ?? option?.raw?.cost_center_id ?? "";

        setFieldValue("customer_id", custId);
        if (setFieldError) setFieldError("customer_id", undefined);

        setFieldValue("cost_center_id", costCenterId ? String(costCenterId) : "");
        if (costCenterId && setFieldError) setFieldError("cost_center_id", undefined);

        setFieldValue("contract_id", "");
        setFieldValue("vehicle_type_id", "");
        setFieldValue("vehicle_id", "");
        setFieldValue("primary_driver_id", "");
        setFieldValue("primary_driver_contact", "");
        setFieldValue("secondary_driver_id", "");
        setFieldValue("secondary_driver_contact", "");
        setFieldValue("helper_id", "");
        setFieldValue("helper_contact", "");

        setContractOptions([]);
        setContractVehicleTypeOptions([]);
        setVehicleOptions([]);
        setAssignedCrew({ primary: null, secondary: null, helper: null });

        if (!custId) return;

        setCustomerDetailLoading(true);
        try {
            const response = await apiService.getCustomerTripDetail(custId);
            const data = response?.data || response || {};

            const apiCostCenterId = data?.cost_center_id || data?.costCenterId || data?.cost_center?.id;
            if (apiCostCenterId) {
                setFieldValue("cost_center_id", String(apiCostCenterId));
                if (setFieldError) setFieldError("cost_center_id", undefined);
            }

            const contracts = (data?.contracts || []).map(c => ({
                value: String(c?.id ?? c?.value ?? c?.contract_id ?? ''),
                label: String(c?.name ?? c?.label ?? `Contract ${c?.id}`)
            })).filter(o => o.value);

            setContractOptions(contracts);
        } catch (err) {
            console.error("Error fetching customer detail:", err);
            errorNotify(err);
        } finally {
            setCustomerDetailLoading(false);
        }
    };

    // 2. Contract Change Handler -> Fetches Vehicle Types
    const handleContractChange = async (option, setFieldValue, setFieldError) => {
        const contractId = option ? option.value : "";
        setFieldValue("contract_id", contractId);
        if (setFieldError) setFieldError("contract_id", undefined);

        setFieldValue("vehicle_type_id", "");
        setFieldValue("vehicle_id", "");
        setFieldValue("primary_driver_id", "");
        setFieldValue("primary_driver_contact", "");
        setFieldValue("secondary_driver_id", "");
        setFieldValue("secondary_driver_contact", "");
        setFieldValue("helper_id", "");
        setFieldValue("helper_contact", "");

        setContractVehicleTypeOptions([]);
        setVehicleOptions([]);
        setAssignedCrew({ primary: null, secondary: null, helper: null });

        if (!contractId) return;

        setContractVehicleTypeLoading(true);
        try {
            const response = await apiService.getContractTripDetail(contractId);
            const vehicleTypes = (response?.data?.vehicle_types || []).map(vt => ({
                value: String(vt?.id ?? vt?.value ?? vt?.vehicle_type_id ?? ''),
                label: String(vt?.title ?? vt?.name ?? vt?.label ?? `Vehicle Type ${vt?.id}`)
            })).filter(o => o.value);

            setContractVehicleTypeOptions(vehicleTypes);
        } catch (err) {
            console.error("Error fetching contract vehicle types:", err);
            errorNotify(err);
        } finally {
            setContractVehicleTypeLoading(false);
        }
    };

    // 3. Vehicle Type Change Handler -> Fetches Vehicles after required filters are selected
    const handleVehicleTypeChange = async (option, values, setFieldValue, setFieldError) => {
        const vTypeId = option ? option.value : "";
        setFieldValue("vehicle_type_id", vTypeId);
        if (setFieldError) setFieldError("vehicle_type_id", undefined);

        await fetchVehiclesByCustomer({ ...values, vehicle_type_id: vTypeId }, setFieldValue);
    };

    // 4. Vehicle Change Handler -> Fetches Vehicle Details (Drivers/Helper)
    const handleVehicleChange = async (option, setFieldValue, setFieldError) => {
        const vehId = option ? option.value : "";
        setFieldValue("vehicle_id", vehId);
        if (setFieldError) setFieldError("vehicle_id", undefined);

        setFieldValue("primary_driver_id", "");
        setFieldValue("primary_driver_contact", "");
        setFieldValue("secondary_driver_id", "");
        setFieldValue("secondary_driver_contact", "");
        setFieldValue("helper_id", "");
        setFieldValue("helper_contact", "");
        setAssignedCrew({ primary: null, secondary: null, helper: null });

        if (!vehId) return;

        // Check raw option for driver info
        const rawPri = extractDriverInfo(option?.raw || option, 'primary');
        const rawSec = extractDriverInfo(option?.raw || option, 'secondary');
        const rawHelp = extractDriverInfo(option?.raw || option, 'helper');

        if (rawPri.id) {
            setFieldValue("primary_driver_id", rawPri.id);
            if (setFieldError) setFieldError("primary_driver_id", undefined);
        }
        if (rawPri.contact) {
            setFieldValue("primary_driver_contact", rawPri.contact);
            if (setFieldError) setFieldError("primary_driver_contact", undefined);
        }
        if (rawSec.id) {
            setFieldValue("secondary_driver_id", rawSec.id);
            if (setFieldError) setFieldError("secondary_driver_id", undefined);
        }
        if (rawSec.contact) {
            setFieldValue("secondary_driver_contact", rawSec.contact);
            if (setFieldError) setFieldError("secondary_driver_contact", undefined);
        }
        if (rawHelp.id) {
            setFieldValue("helper_id", rawHelp.id);
            if (setFieldError) setFieldError("helper_id", undefined);
        }
        if (rawHelp.contact) {
            setFieldValue("helper_contact", rawHelp.contact);
            if (setFieldError) setFieldError("helper_contact", undefined);
        }

        setAssignedCrew({
            primary: rawPri.id ? rawPri : null,
            secondary: rawSec.id ? rawSec : null,
            helper: rawHelp.id ? rawHelp : null,
        });

        try {
            const response = await apiService.getVehicleDetailsByFuelLog(vehId);
            const detailData = response?.data || response || {};

            const apiPri = extractDriverInfo(detailData, 'primary');
            const apiSec = extractDriverInfo(detailData, 'secondary');
            const apiHelp = extractDriverInfo(detailData, 'helper');

            const finalPri = apiPri.id ? apiPri : rawPri;
            const finalSec = apiSec.id ? apiSec : rawSec;
            const finalHelp = apiHelp.id ? apiHelp : rawHelp;

            if (finalPri.id) {
                setFieldValue("primary_driver_id", finalPri.id);
                if (setFieldError) setFieldError("primary_driver_id", undefined);
            }
            if (finalPri.contact) {
                setFieldValue("primary_driver_contact", finalPri.contact);
                if (setFieldError) setFieldError("primary_driver_contact", undefined);
            }

            if (finalSec.id) {
                setFieldValue("secondary_driver_id", finalSec.id);
                if (setFieldError) setFieldError("secondary_driver_id", undefined);
            }
            if (finalSec.contact) {
                setFieldValue("secondary_driver_contact", finalSec.contact);
                if (setFieldError) setFieldError("secondary_driver_contact", undefined);
            }

            if (finalHelp.id) {
                setFieldValue("helper_id", finalHelp.id);
                if (setFieldError) setFieldError("helper_id", undefined);
            }
            if (finalHelp.contact) {
                setFieldValue("helper_contact", finalHelp.contact);
                if (setFieldError) setFieldError("helper_contact", undefined);
            }

            setAssignedCrew({
                primary: finalPri.id ? finalPri : null,
                secondary: finalSec.id ? finalSec : null,
                helper: finalHelp.id ? finalHelp : null,
            });
        } catch (err) {
            console.error("Error fetching vehicle details:", err);
        }
    };

    // Dropdown mobiles come back in whatever shape the user record holds (0300…, +92…),
    // so normalize before filling the field or the format validation rejects our own value.
    const handlePrimaryDriverChange = (option, setFieldValue, setFieldError) => {
        const dId = option ? option.value : "";
        setFieldValue("primary_driver_id", dId);
        if (setFieldError) setFieldError("primary_driver_id", undefined);
        const rawMobile = option?.mobile || option?.phone || option?.contact || option?.raw?.mobile || option?.raw?.phone || "";
        const normMobile = normalizePhoneNumberValue(rawMobile);
        setFieldValue("primary_driver_contact", normMobile || "");
        if (normMobile && setFieldError) setFieldError("primary_driver_contact", undefined);
    };

    const handleSecondaryDriverChange = (option, setFieldValue, setFieldError) => {
        const dId = option ? option.value : "";
        setFieldValue("secondary_driver_id", dId);
        if (setFieldError) setFieldError("secondary_driver_id", undefined);
        const rawMobile = option?.mobile || option?.phone || option?.contact || option?.raw?.mobile || option?.raw?.phone || "";
        const normMobile = normalizePhoneNumberValue(rawMobile);
        setFieldValue("secondary_driver_contact", normMobile || "");
        if (normMobile && setFieldError) setFieldError("secondary_driver_contact", undefined);
    };

    const handleHelperChange = (option, setFieldValue, setFieldError) => {
        const hId = option ? option.value : "";
        setFieldValue("helper_id", hId);
        if (setFieldError) setFieldError("helper_id", undefined);
        if (option?.mobile) {
            const normMobile = normalizePhoneNumberValue(option.mobile);
            setFieldValue("helper_contact", normMobile);
            if (normMobile && setFieldError) setFieldError("helper_contact", undefined);
        }
    };

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        setSubmitError(null);
        try {
            const payload = {
                start_at: values.start_at,
                expected_end_at: values.expected_end_at,
                cost_center_id: Number(values.cost_center_id),
                customer_id: Number(values.customer_id),
                contract_id: Number(values.contract_id),
                vehicle_type_id: Number(values.vehicle_type_id),
                vehicle_id: Number(values.vehicle_id),
                primary_driver_id: Number(values.primary_driver_id),
                primary_driver_contact: String(values.primary_driver_contact || ""),
            };

            if (values.secondary_driver_id) {
                payload.secondary_driver_id = Number(values.secondary_driver_id);
                payload.secondary_driver_contact = String(values.secondary_driver_contact || "");
            }

            if (values.helper_id) {
                payload.helper_id = Number(values.helper_id);
                payload.helper_contact = String(values.helper_contact || "");
            }

            const res = await apiService.createTripStage(payload);
            successNotify("Trip Created Successfully!");

            const newTripData = res?.data || res;
            if (onTripCreated) {
                onTripCreated(newTripData);
            }
            resetForm();
            onClose();

            const newTripId =
                newTripData?.id ||
                newTripData?.trip_id ||
                newTripData?.trip?.id;
            if (newTripId) {
                navigate(`/trips-list/planning/${newTripId}`);
            }
        } catch (err) {
            const rawMsg =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err?.response?.data?.msg ||
                err?.message ||
                "Failed to create trip";
            const displayMsg = Array.isArray(rawMsg) ? rawMsg.join(", ") : String(rawMsg);
            setSubmitError(displayMsg);
            errorNotify(err);
        } finally {
            setSubmitting(false);
        }
    };

    const portalTarget = typeof document !== 'undefined' ? document.body : null;
    const selectCustomStyles = {
        menuPortal: (base) => ({
            ...base,
            zIndex: 999999,
        }),
        menu: (base) => ({
            ...base,
            zIndex: 999999,
        }),
    };

    return (
        <div
            className="create_trip_drawer_overlay"
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}
        >
            <div className="create_trip_drawer_container" onClick={(e) => e.stopPropagation()}>
                {/* Header Bar */}
                <div className="create_trip_drawer_header">
                    <div className="drawer_header_title_box">
                        <button className="mobile_drawer_back_btn" onClick={onClose} type="button" title="Back">
                            <FiArrowLeft />
                        </button>
                        <h2>Create New Trip</h2>
                    </div>
                    <button className="desktop_drawer_close_btn" onClick={onClose} type="button" title="Close Drawer">
                        <IoClose />
                    </button>
                </div>

                {/* Banner Box */}
                <div className="create_trip_banner_box">
                    <span>Trip Stage Creation</span>
                </div>

                {/* Inline Submit Error Alert */}
                {submitError && (
                    <div className="create_trip_error_alert">
                        <FiAlertCircle size={18} className="create_trip_error_icon" />
                        <span>{submitError}</span>
                        <button
                            type="button"
                            onClick={() => setSubmitError(null)}
                            className="create_trip_error_close"
                            title="Dismiss error"
                        >
                            <IoClose size={16} />
                        </button>
                    </div>
                )}

                {/* Formik Form Container */}
                <Formik
                    initialValues={initialValues}
                    validationSchema={createTripDrawerSchema}
                    onSubmit={handleSubmit}
                >
                    {({ values, errors, touched, handleBlur, setFieldValue, setFieldError, isSubmitting }) => (
                        <Form className="create_trip_drawer_form">
                            <div className="create_trip_form_scroll_body">
                                {/* Row 1: Customer & Cost Center */}
                                <Row className="drawer_form_row">
                                    <Col md={6} xs={12}>
                                        <Select
                                            label="Customer"
                                            name="customer_id"
                                            placeholder="Select Customer"
                                            options={customerOptions}
                                            value={customerOptions.find(opt => String(opt.value) === String(values.customer_id)) || null}
                                            onChange={(option) => handleCustomerChange(option, setFieldValue, setFieldError)}
                                            onBlur={handleBlur}
                                            error={!values.customer_id ? errors.customer_id : undefined}
                                            touched={touched.customer_id}
                                            required={true}
                                            loading={reduxLoading}
                                            menuPortalTarget={portalTarget}
                                            customStyles={selectCustomStyles}
                                            isSearch={true}
                                        />
                                    </Col>
                                    <Col md={6} xs={12}>
                                        <Select
                                            label="Cost Center"
                                            name="cost_center_id"
                                            placeholder="Select Cost Center"
                                            options={costCenterOptions}
                                            value={costCenterOptions.find(opt => String(opt.value) === String(values.cost_center_id)) || null}
                                            onChange={(opt) => {
                                                setFieldValue("cost_center_id", opt ? opt.value : "");
                                                setFieldError("cost_center_id", undefined);
                                            }}
                                            onBlur={handleBlur}
                                            error={!values.cost_center_id ? errors.cost_center_id : undefined}
                                            touched={touched.cost_center_id}
                                            required={false}
                                            loading={reduxLoading}
                                            isDisabled={true}
                                            menuPortalTarget={portalTarget}
                                            customStyles={selectCustomStyles}
                                            isSearch={true}
                                        />
                                    </Col>
                                </Row>

                                {/* Row 2: Contract & Vehicle Type */}
                                <Row className="drawer_form_row">
                                    <Col md={6} xs={12}>
                                        <Select
                                            label="Contract"
                                            name="contract_id"
                                            placeholder={values.customer_id ? "Select Contract" : "Select Customer First"}
                                            options={contractOptions}
                                            value={contractOptions.find(opt => String(opt.value) === String(values.contract_id)) || null}
                                            onChange={(option) => handleContractChange(option, setFieldValue, setFieldError)}
                                            onBlur={handleBlur}
                                            error={!values.contract_id ? errors.contract_id : undefined}
                                            touched={touched.contract_id}
                                            required={true}
                                            loading={customerDetailLoading}
                                            isDisabled={!values.customer_id || customerDetailLoading}
                                            menuPortalTarget={portalTarget}
                                            customStyles={selectCustomStyles}
                                            isSearch={true}
                                        />
                                    </Col>
                                    <Col md={6} xs={12}>
                                        <Select
                                            label="Vehicle Type"
                                            name="vehicle_type_id"
                                            placeholder={values.contract_id ? "Select Vehicle Type" : "Select Contract First"}
                                            options={contractVehicleTypeOptions}
                                            value={contractVehicleTypeOptions.find(opt => String(opt.value) === String(values.vehicle_type_id)) || null}
                                            onChange={(option) => handleVehicleTypeChange(option, values, setFieldValue, setFieldError)}
                                            onBlur={handleBlur}
                                            error={!values.vehicle_type_id ? errors.vehicle_type_id : undefined}
                                            touched={touched.vehicle_type_id}
                                            required={true}
                                            loading={contractVehicleTypeLoading}
                                            isDisabled={!values.contract_id || contractVehicleTypeLoading}
                                            menuPortalTarget={portalTarget}
                                            customStyles={selectCustomStyles}
                                            isSearch={true}
                                        />
                                    </Col>
                                </Row>

                                {/* Row 3: Trip Dates */}
                                <Row className="drawer_form_row">
                                    <Col md={6} xs={12}>
                                        <Input
                                            label="Trip Start Date"
                                            name="start_at"
                                            type="datetime-local"
                                            value={values.start_at}
                                            onChange={(event) =>
                                                handleTripDateChange("start_at", event.target.value, values, setFieldValue)
                                            }
                                            onBlur={handleBlur}
                                            error={errors.start_at}
                                            touched={touched.start_at}
                                            required={true}
                                        />
                                    </Col>
                                    <Col md={6} xs={12}>
                                        <Input
                                            label="Expected End Date"
                                            name="expected_end_at"
                                            type="datetime-local"
                                            value={values.expected_end_at}
                                            onChange={(event) =>
                                                handleTripDateChange("expected_end_at", event.target.value, values, setFieldValue)
                                            }
                                            onBlur={handleBlur}
                                            error={errors.expected_end_at}
                                            touched={touched.expected_end_at}
                                            required={true}
                                        />
                                    </Col>
                                </Row>

                                {/* Row 4: Vehicle */}
                                <Row className="drawer_form_row">
                                    <Col xs={12}>
                                        <Select
                                            label="Vehicle"
                                            name="vehicle_id"
                                            placeholder={
                                                !values.customer_id
                                                    ? "Select Customer First"
                                                    : !values.contract_id
                                                        ? "Select Contract First"
                                                        : !values.vehicle_type_id
                                                            ? "Select Vehicle Type First"
                                                            : !values.start_at || !values.expected_end_at
                                                                ? "Select Trip Dates First"
                                                                : vehicleOptionsLoading
                                                                    ? "Loading Vehicles..."
                                                                    : vehicleOptions.length === 0
                                                                        ? "No Vehicles Available"
                                                                        : "Select Vehicle"
                                            }
                                            options={vehicleOptions}
                                            value={vehicleOptions.find(opt => String(opt.value) === String(values.vehicle_id)) || null}
                                            onChange={(option) => handleVehicleChange(option, setFieldValue, setFieldError)}
                                            onBlur={handleBlur}
                                            error={!values.vehicle_id ? errors.vehicle_id : undefined}
                                            touched={touched.vehicle_id}
                                            required={true}
                                            loading={vehicleOptionsLoading}
                                            isDisabled={!areVehicleDropdownFiltersReady(values) || vehicleOptionsLoading}
                                            menuPortalTarget={portalTarget}
                                            customStyles={selectCustomStyles}
                                            isSearch={true}
                                        />
                                    </Col>
                                </Row>

                                {/* Row 5: Primary Driver & Primary Driver Contact */}
                                <Row className="drawer_form_row">
                                    <Col md={6} xs={12}>
                                        <Select
                                            label="Primary Driver"
                                            name="primary_driver_id"
                                            placeholder="Select Driver"
                                            options={userOptions}
                                            value={userOptions.find(opt => String(opt.value) === String(values.primary_driver_id)) || null}
                                            onChange={(option) => handlePrimaryDriverChange(option, setFieldValue, setFieldError)}
                                            onBlur={handleBlur}
                                            error={!values.primary_driver_id ? errors.primary_driver_id : undefined}
                                            touched={touched.primary_driver_id}
                                            required={true}
                                            loading={reduxLoading}
                                            menuPortalTarget={portalTarget}
                                            customStyles={selectCustomStyles}
                                            isSearch={true}
                                        />
                                    </Col>
                                    <Col md={6} xs={12}>
                                        <PhoneNumberInput
                                            label="Primary Driver Contact"
                                            name="primary_driver_contact"
                                            value={values.primary_driver_contact}
                                            setFieldValue={(name, val) => {
                                                setFieldValue(name, val);
                                                if (val) setFieldError(name, undefined);
                                            }}
                                            onBlur={handleBlur}
                                            error={values.primary_driver_contact && errors.primary_driver_contact?.toLowerCase?.().includes('required') ? undefined : errors.primary_driver_contact}
                                            touched={touched.primary_driver_contact}
                                            required={true}
                                        />
                                    </Col>
                                </Row>

                                {/* Row 6 (Conditional): Secondary Driver & Contact */}
                                {values.secondary_driver_id && (
                                    <Row className="drawer_form_row">
                                        <Col md={6} xs={12}>
                                            <Select
                                                label="Secondary Driver"
                                                name="secondary_driver_id"
                                                placeholder="Select Secondary Driver"
                                                options={userOptions}
                                                value={userOptions.find(opt => String(opt.value) === String(values.secondary_driver_id)) || null}
                                                onChange={(option) => handleSecondaryDriverChange(option, setFieldValue, setFieldError)}
                                                onBlur={handleBlur}
                                                error={!values.secondary_driver_id ? errors.secondary_driver_id : undefined}
                                                touched={touched.secondary_driver_id}
                                                required={false}
                                                loading={reduxLoading}
                                                menuPortalTarget={portalTarget}
                                                customStyles={selectCustomStyles}
                                                isSearch={true}
                                            />
                                        </Col>
                                        <Col md={6} xs={12}>
                                            <PhoneNumberInput
                                                label="Secondary Driver Contact"
                                                name="secondary_driver_contact"
                                                value={values.secondary_driver_contact}
                                                setFieldValue={(name, val) => {
                                                    setFieldValue(name, val);
                                                    if (val) setFieldError(name, undefined);
                                                }}
                                                onBlur={handleBlur}
                                                error={values.secondary_driver_contact && errors.secondary_driver_contact?.toLowerCase?.().includes('required') ? undefined : errors.secondary_driver_contact}
                                                touched={touched.secondary_driver_contact}
                                                required={false}
                                            />
                                        </Col>
                                    </Row>
                                )}

                                {/* Row 7 (Conditional): Helper & Contact */}
                                {values.helper_id && (
                                    <Row className="drawer_form_row">
                                        <Col md={6} xs={12}>
                                            <Select
                                                label="Helper"
                                                name="helper_id"
                                                placeholder="Select Helper"
                                                options={userOptions}
                                                value={userOptions.find(opt => String(opt.value) === String(values.helper_id)) || null}
                                                onChange={(option) => handleHelperChange(option, setFieldValue, setFieldError)}
                                                onBlur={handleBlur}
                                                error={!values.helper_id ? errors.helper_id : undefined}
                                                touched={touched.helper_id}
                                                required={false}
                                                loading={reduxLoading}
                                                menuPortalTarget={portalTarget}
                                                customStyles={selectCustomStyles}
                                                isSearch={true}
                                            />
                                        </Col>
                                        <Col md={6} xs={12}>
                                            <PhoneNumberInput
                                                label="Helper Contact"
                                                name="helper_contact"
                                                value={values.helper_contact}
                                                setFieldValue={(name, val) => {
                                                    setFieldValue(name, val);
                                                    if (val) setFieldError(name, undefined);
                                                }}
                                                onBlur={handleBlur}
                                                error={values.helper_contact && errors.helper_contact?.toLowerCase?.().includes('required') ? undefined : errors.helper_contact}
                                                touched={touched.helper_contact}
                                                required={false}
                                            />
                                        </Col>
                                    </Row>
                                )}
                            </div>

                            {/* Footer Submit Button Bar */}
                            <div className="create_trip_drawer_footer">
                                <Button
                                    submitText={isSubmitting ? "Creating..." : "Create"}
                                    type="submit"
                                    shouldRow={false}
                                    isShowCancel={false}
                                    loading={isSubmitting}
                                />
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};

export default CreateTripDrawer;

